import React, { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import * as THREE from 'three';

/**
 * Renders AI-generated Three.js HTML code in a sandboxed iframe
 * Features:
 * - Safe execution of Three.js code
 * - Automatic Three.js library injection
 * - Error handling and fallback UI
 * - Cleanup on unmount
 * - Responsive sizing with constraints
 */
function ThreeJsRendererContent({ code }) {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!code || !iframeRef.current) return;

    let iframeCleanedUp = false;

    try {
      const iframe = iframeRef.current;

      // Send cleanup message to previous iframe content before clearing
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage({ type: 'cleanup' }, '*');
      }

      // Clear iframe completely before writing new content
      iframe.src = 'about:blank';

      // Wait for iframe to be cleared, then write new content
      const initTimer = setTimeout(() => {
        if (iframeCleanedUp) return;

        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

        // Inject THREE into iframe's window context BEFORE writing HTML
        iframe.contentWindow.THREE = THREE;

        // Extract HTML and JavaScript from the code block
        // Code format: ```threejs\n<div>...</div>\n<script>...</script>\n```
        let cleanCode = code;

        // Remove code fence markers if present
        cleanCode = cleanCode.replace(/```threejs\s*/gi, '').replace(/```\s*$/gi, '');

        // Extract div container (everything before <script>)
        const divMatch = cleanCode.match(/<div[^>]*>.*?<\/div>/is);
        const containerHTML = divMatch ? divMatch[0] : '<div id="container" style="width: 100%; height: 100%;"></div>';

        // Extract script content (everything between <script> and </script>)
        const scriptMatch = cleanCode.match(/<script[^>]*>([\s\S]*?)<\/script>/i);
        const scriptContent = scriptMatch ? scriptMatch[1] : cleanCode;

        // Wrap the script in an IIFE to avoid variable scope issues
        const wrappedScript = `<script>(function() { ${scriptContent} })();</script>`;

        // Create complete HTML document (Three.js is now injected via iframe.contentWindow.THREE)
        const fullHTML = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      width: 100%;
      height: 100vh;
      overflow: hidden;
      background: transparent;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    canvas {
      display: block;
      width: 100%;
      height: 100%;
    }
  </style>
</head>
<body>
  ${containerHTML}
  ${wrappedScript}
  <script>
    // Suppress Three.js console warnings to prevent flooding
    const originalWarn = console.warn;
    const originalError = console.error;

    console.warn = function(...args) {
      const msg = args.join(' ');
      // Suppress common Three.js geometry warnings
      if (msg.includes('BufferGeometry') ||
          msg.includes('Buffer size') ||
          msg.includes('dispose()')) {
        return;
      }
      originalWarn.apply(console, args);
    };

    console.error = function(...args) {
      const msg = args.join(' ');
      // Suppress common Three.js geometry errors
      if (msg.includes('BufferGeometry') ||
          msg.includes('Buffer size') ||
          msg.includes('dispose()') ||
          msg.includes('WebGL context')) {
        return;
      }
      originalError.apply(console, args);
    };

    // Cleanup function for WebGL context
    window.cleanupThreeJS = function() {
      if (window.renderer) {
        window.renderer.dispose();
        window.renderer.forceContextLoss();
        window.renderer = null;
      }
      if (window.scene) {
        window.scene.traverse((object) => {
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(material => material.dispose());
            } else {
              object.material.dispose();
            }
          }
        });
        window.scene = null;
      }
    };

    // Listen for cleanup message from parent
    window.addEventListener('message', function(e) {
      if (e.data.type === 'cleanup') {
        window.cleanupThreeJS();
      }
    });

    // Error handling
    window.addEventListener('error', function(e) {
      parent.postMessage({ type: 'error', message: e.message }, '*');
    });

    // Notify parent when loaded
    window.addEventListener('load', function() {
      parent.postMessage({ type: 'loaded' }, '*');
    });
  </script>
</body>
</html>
        `;

        // Write the HTML to the iframe
        iframeDoc.open();
        iframeDoc.write(fullHTML);
        iframeDoc.close();

        // Set loading to false after a short delay
        const loadTimer = setTimeout(() => {
          if (!iframeCleanedUp) {
            setIsLoading(false);
          }
        }, 1000);

        return () => clearTimeout(loadTimer);
      }, 10);

      return () => {
        iframeCleanedUp = true;
        clearTimeout(initTimer);
        // Send cleanup message when effect is cleaned up
        if (iframe.contentWindow) {
          iframe.contentWindow.postMessage({ type: 'cleanup' }, '*');
        }
      };
    } catch (err) {
      console.error('ThreeJsRenderer error:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, [code]);

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'error') {
        setError(event.data.message);
        setIsLoading(false);
      } else if (event.data.type === 'loaded') {
        setIsLoading(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Handle resize to maintain canvas size
  useEffect(() => {
    if (!containerRef.current || !iframeRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        // Notify iframe about resize
        if (iframeRef.current && iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            { type: 'resize', width, height },
            '*'
          );
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-red-50 dark:bg-red-900/20 rounded-lg border-2 border-red-200 dark:border-red-800">
        <svg
          className="w-12 h-12 text-red-500 dark:text-red-400 mb-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">
          Failed to render Three.js visual
        </p>
        <p className="text-xs text-red-600 dark:text-red-400 text-center max-w-md">
          {error}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full"
      style={{
        minHeight: '400px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-200 border-t-indigo-600 dark:border-gray-600 dark:border-t-indigo-400"></div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Loading interactive visual...</p>
          </div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        title="Three.js Interactive Visual"
        className="w-full h-full rounded-lg border-0"
        style={{
          flex: 1,
          minHeight: '400px',
        }}
        sandbox="allow-scripts allow-same-origin"
      />
    </div>
  );
}

/**
 * Error fallback component
 */
function ErrorFallback({ error }) {
  return (
    <div className="flex flex-col items-center justify-center p-8 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border-2 border-yellow-200 dark:border-yellow-800">
      <svg
        className="w-12 h-12 text-yellow-600 dark:text-yellow-400 mb-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
        Unable to load Three.js visual
      </p>
      <p className="text-xs text-yellow-600 dark:text-yellow-400 text-center max-w-md">
        {error.message || 'An unexpected error occurred'}
      </p>
    </div>
  );
}

/**
 * Main component with error boundary
 */
export function ThreeJsRenderer({ code }) {
  if (!code || typeof code !== 'string') {
    return (
      <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          No Three.js code provided
        </p>
      </div>
    );
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ThreeJsRendererContent code={code} />
    </ErrorBoundary>
  );
}
